import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Spinner } from '@nextui-org/react';
import { useGetAiWorkersByCategoryQuery, useDeleteAiWorkersMutation, useEntityResourcePermissions } from '@circuitry-ai/doc-data';
import {
  CardListViewMode,
  CtyButton,
  CtyShowToast,
  EntityCardWithActions,
  EntityListView,
  ListActionsCellWithDelete,
  checkType,
  getActionsColumnDef,
  getDescriptionColumnDef,
  getNameColumnDef,
} from '@circuitry-ai/doc-ui';
import { Icon } from '@iconify/react';
import { FormGenieWorker } from './types';
import { useCallback, useMemo, useState } from 'react';

const FORMS_TAB = 'forms';

interface FormGenieWorkerListProps {
  basePath?: string;
}

function useDeleteFormGenieHandler(worker: FormGenieWorker, refetch: () => void) {
  const [deleteAiWorkers, { isLoading: deleteFormGenieLoading }] = useDeleteAiWorkersMutation();

  const handleConfirmDelete = useCallback(async (closeModal: () => void) => {
    try {
      const response = await deleteAiWorkers(worker.aiworker_id);
      CtyShowToast(checkType.success, response?.data);
      closeModal();
      refetch();
    } catch (error) {
      CtyShowToast(checkType.error, (error as Error)?.message ?? 'Delete failed');
    }
  }, [deleteAiWorkers, refetch, worker.aiworker_id]);

  return { handleConfirmDelete, isDeleteLoading: deleteFormGenieLoading };
}

const FormGenieWorkerCard = ({
  worker,
  basePath,
  refetch,
}: {
  readonly worker: FormGenieWorker;
  readonly basePath: string;
  readonly refetch: () => void;
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { canDelete } = useEntityResourcePermissions('uxgenie', worker.aiworker_id);
  const { handleConfirmDelete, isDeleteLoading } = useDeleteFormGenieHandler(worker, refetch);

  const footerItems = (
    <CtyButton
      className="w-full border-1"
      variant="solid"
      radius="sm"
      size="lg"
      color="primary"
      onPress={() =>
        navigate(`${basePath}/${worker.aiworker_id}?tab=${FORMS_TAB}`, {
          state: { formGenieWorker: worker },
        })
      }
    >
      {t('Launch')}
    </CtyButton>
  );

  return (
    <EntityCardWithActions
      id={worker.aiworker_id}
      name={worker.name}
      description={worker.description}
      footerItems={footerItems}
      canDelete={canDelete}
      dropdownAriaLabel="FormGenie Actions"
      deleteModalEntityName={worker.name}
      onConfirmDelete={handleConfirmDelete}
      isDeleteLoading={isDeleteLoading}
      deleteConfirmLabel={t('Delete FormGenie worker')}
    />
  );
};

const FormGenieActionsCell = ({ worker, basePath, refetch }: { readonly worker: FormGenieWorker; readonly basePath: string; readonly refetch: () => void }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { canDelete } = useEntityResourcePermissions('uxgenie', worker.aiworker_id);
  const { handleConfirmDelete, isDeleteLoading } = useDeleteFormGenieHandler(worker, refetch);

  return (
    <ListActionsCellWithDelete
      actions={[
        {
          label: t('Launch'),
          onPress: () =>
            navigate(`${basePath}/${worker.aiworker_id}?tab=${FORMS_TAB}`, {
              state: { formGenieWorker: worker },
            }),
          variant: 'solid',
          color: 'primary',
        },
      ]}
      canDelete={canDelete}
      entityName={worker.name}
      onConfirmDelete={handleConfirmDelete}
      isDeleteLoading={isDeleteLoading}
      deleteConfirmLabel={t('Delete FormGenie worker')}
    />
  );
};

const renderFormGenieActionsCell = (basePath: string, refetch: () => void) =>
  function FormGenieActionsRenderer(params: any) {
    return <FormGenieActionsCell worker={params?.data} basePath={basePath} refetch={refetch} />;
  };

export default function FormGenieWorkerList({ basePath = '/formgenie' }: Readonly<FormGenieWorkerListProps>) {
  const { t } = useTranslation();
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [viewMode, setViewMode] = useState<CardListViewMode>(CardListViewMode.GRID);

  const { data: formGenieWorkers, isLoading, isError, refetch } = useGetAiWorkersByCategoryQuery(
    { category: 'uxgenie' as any },
    { refetchOnMountOrArgChange: true }
  );

  const workers = useMemo(() => {
    const allWorkers = (formGenieWorkers || []) as FormGenieWorker[];
    return allWorkers.filter((w) => w.name?.toLowerCase() === 'formgenie');
  }, [formGenieWorkers]);

  const filteredWorkers = useMemo(() => {
    if (!searchQuery.trim()) return workers;
    return workers.filter((w) =>
      w.name?.toLowerCase().includes(searchQuery.toLowerCase())
    );
  }, [workers, searchQuery]);

  const columnDefs = useMemo(
    () => [
      getNameColumnDef(t('Name')),
      getDescriptionColumnDef(t('Description')),
      getActionsColumnDef({
        headerName: t('Actions'),
        minWidth: 200,
        maxWidth: 240,
        cellRenderer: renderFormGenieActionsCell(basePath, refetch),
      }),
    ],
    [t, basePath, refetch]
  );

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <Spinner size="sm" label={t('Loading FormGenie Workers...')} />
      </div>
    );
  }

  if (isError) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-danger">{t('Error loading FormGenie Workers')}</div>
      </div>
    );
  }

  if (workers.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-full gap-4">
        <Icon icon="mdi:form-select" className="text-default-400" width={64} height={64} />
        <p className="text-default-500">{t('No FormGenie workers found')}</p>
      </div>
    );
  }

  return (
    <EntityListView
      items={filteredWorkers}
      isLoading={false}
      searchId="formgenie-search"
      searchPlaceholder={t('Search FormGenie workers')}
      onSearch={setSearchQuery}
      viewMode={viewMode}
      onViewModeChange={setViewMode}
      emptyMessage={t('No FormGenie workers found')}
      cardKey={(worker: FormGenieWorker) => worker.aiworker_id}
      renderCard={(worker: FormGenieWorker) => (
        <FormGenieWorkerCard worker={worker} basePath={basePath} refetch={refetch} />
      )}
      tableId="formgenie-list-table"
      columnDefs={columnDefs}
    />
  );
}
